import type { FloodCategory } from '../config/gauges';
import type { LatLng } from '../lib/geo';

/** A gauge point from the NWS reference ArcGIS layer plus cached NWPS status (Gauge.swift). */
export interface Gauge {
  id: string;
  name: string;
  lat: number;
  lng: number;
  drainageAreaKm: number | null;
  observationSeverity: FloodCategory | null;
  forecastSeverity: FloodCategory | null;
  forecastPeakTime: Date | null;
  forecastIsRising: boolean | null;
  nextStage: FloodCategory | null;
  nextStageTime: Date | null;
  /** Precomputed at publish time so pins get stable inputs. */
  hoursToCrest: number | null;
  hoursToNextStage: number | null;
}

export function gaugeLatLng(g: Gauge): LatLng {
  return { lat: g.lat, lng: g.lng };
}

/** iOS GaugeAnnotationMapContent.isFlooding: observed at or above action (or low water/record), else forecast flooding. */
export function gaugeIsFlooding(g: Pick<Gauge, 'observationSeverity' | 'forecastSeverity'>): boolean {
  switch (g.observationSeverity) {
    case 'action':
    case 'low_water':
    case 'major':
    case 'minor':
    case 'moderate':
    case 'record':
      return true;
    default: {
      const f = g.forecastSeverity;
      return f === 'minor' || f === 'moderate' || f === 'major' || f === 'record';
    }
  }
}

// ---- NWPS /gauges/{lid} ----

export interface NWPSStatusEntry {
  primary?: number | null;
  primaryUnit?: string | null;
  secondary?: number | null;
  secondaryUnit?: string | null;
  floodCategory?: string | null;
  validTime?: string | null;
}

export interface NWPSThreshold {
  stage?: number | null;
  flow?: number | null;
}

export interface NWPSFloodCategories {
  action?: NWPSThreshold | null;
  minor?: NWPSThreshold | null;
  moderate?: NWPSThreshold | null;
  major?: NWPSThreshold | null;
}

export interface NWPSGaugeResponse {
  lid: string;
  name: string;
  usgsId?: string | null;
  latitude?: number;
  longitude?: number;
  state?: { abbreviation?: string; name?: string } | null;
  county?: string | null;
  timeZone?: string | null;
  status: {
    observed?: NWPSStatusEntry | null;
    forecast?: (NWPSStatusEntry & { maxPrimary?: number | null; maxPrimaryUnit?: string | null }) | null;
  };
  flood?: { categories?: NWPSFloodCategories | null } | null;
}

// ---- NWPS /gauges/{lid}/stageflow ----

export interface StageFlowPoint {
  validTime: string;
  primary: number;
  secondary?: number | null;
}

export interface StageFlowSeries {
  primaryName?: string | null;
  primaryUnits?: string | null;
  secondaryName?: string | null;
  secondaryUnits?: string | null;
  issuedTime?: string | null;
  data: StageFlowPoint[];
}

export interface StageFlowResponse {
  observed?: StageFlowSeries | null;
  forecast?: StageFlowSeries | null;
}

/** Result of one NWPS status load (GaugeSeverityResult). */
export interface GaugeSeverityResult {
  gaugeID: string;
  gaugeName: string;
  observed: FloodCategory;
  observedStage: number | null;
  observedUnit: string | null;
  observedValidTime: string | null;
  forecast: FloodCategory;
  forecastStage: number | null;
  forecastUnit: string | null;
  forecastValidTime: string | null;
  forecastPeakTime: Date | null;
  forecastIsRising: boolean | null;
  nextStage: FloodCategory | null;
  nextStageTime: Date | null;
}
